/**
 * Global promise-based confirm dialog.
 *
 * Beth's Feb-2026 UX ask: replace every ``window.confirm`` in the app with a
 * consistent Shadcn dialog. Native browser popups can be suppressed by
 * Chrome on rapid clicks and behave inconsistently on iPad Safari — a real
 * risk of a destructive action being silently lost.
 *
 * Usage (one-liner replacement):
 *
 *     // before
 *     if (!window.confirm('Delete this order?')) return;
 *
 *     // after — anywhere in a component tree wrapped by <ConfirmProvider>
 *     if (!await confirm('Delete this order?')) return;
 *
 * Options:
 *     await confirm({
 *       title: 'Delete order?',
 *       description: 'This cannot be undone.',
 *       confirmLabel: 'Delete',
 *       cancelLabel: 'Keep',
 *       destructive: true,
 *     });
 */
import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmCtx = React.createContext(null);

const DEFAULT_STATE = {
  open: false,
  title: "Are you sure?",
  description: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  destructive: false,
  resolve: null,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = React.useState(DEFAULT_STATE);

  const confirm = React.useCallback((optsOrMessage) => {
    // Support both confirm("message") and confirm({ title, description, ... })
    const opts =
      typeof optsOrMessage === "string"
        ? { description: optsOrMessage }
        : optsOrMessage || {};
    return new Promise((resolve) => {
      setState({
        ...DEFAULT_STATE,
        ...opts,
        open: true,
        resolve,
      });
    });
  }, []);

  const handleClose = (result) => {
    const { resolve } = state;
    setState((s) => ({ ...s, open: false }));
    // Resolve on next tick so the dialog can animate out before the caller's
    // .then() fires (avoids janky "dialog is still visible" flashes).
    setTimeout(() => resolve && resolve(result), 0);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog
        open={state.open}
        onOpenChange={(o) => {
          if (!o) handleClose(false);
        }}
      >
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-dialog-title">
              {state.title}
            </AlertDialogTitle>
            {state.description ? (
              <AlertDialogDescription data-testid="confirm-dialog-description">
                {state.description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-dialog-cancel"
              onClick={() => handleClose(false)}
            >
              {state.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={() => handleClose(true)}
              className={
                state.destructive
                  ? "bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  : undefined
              }
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const confirm = React.useContext(ConfirmCtx);
  if (!confirm) {
    throw new Error(
      "useConfirm must be used inside a <ConfirmProvider>. Wrap your App with it."
    );
  }
  return confirm;
}
